import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { next, later } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import ScrollingUtils from 'mewe/utils/scrolling-utils';

/**
 * USAGE:
 *
 * 1. Import the dropdown installer utility to component that shows dropdown:
 *      import { installDropdown } from 'mewe/utils/dropdown-utils';
 *
 * 2. Add a tracked field that toggles showing the dropdown:
 *      @tracked dropdownOptions;
 *
 * 3. Call the installer in component's constructor, passing the tracked field name.
 *    It will create `setDropdownRef` function in the component that will be used to toggle the dropdown:
 *
 *      installDropdown(this, 'dropdownOptions');
 *
 * 4. In your template, wrap the dropdown in an {{#if}} statement that checks the tracked field
 *    and attach setDropdownRef to the opener button and the dropdown menu:
 *
 *      <ContainerElement class="p-relative"> <!-- Important: relative position for dropdown alignment -->
 *        <OpenerElement {{on "click" (fn this.setDropdownRef (not this.dropdownOptions))}} />
 *        {{#if this.dropdownOptions}}
 *          <BasicUi::MwDropdownMenu @setDropdownRef={{this.setDropdownRef}}>
 *            <!-- dropdown content -->
 *          </BasicUi::MwDropdownMenu>
 *        {{/if}}
 *      </ContainerElement>
 */

export default class MwDropdownMenu extends Component {

  @tracked isExpanded = false;

  duration = 200;

  constructor() {
    super(...arguments);

    next(() => {
      // provide close function to parent
      this.args.setDropdownRef?.(this.closeDropdown.bind(this));
    });
  }

  @action
  dropdownWillDestroy() {
    // in case of incorrect dropdown closing that doesn't remove dropdown--active class
    document.querySelector('.c-new-navigation')?.classList.remove('dropdown--active');
  }

  @action
  onMouseLeave() {
    if (this.args.trigger === 'mouseEnter') {
      this.args.setDropdownRef?.(false);
    }
  }

  @computed(
    'args.rightOffset',
    'args.leftOffset',
    'args.topTranslateMenu',
    'args.bottomTranslateMenu',
    'args.ignoreRTL'
  )
  get translateMenu() {
    let isRtl = document.dir,
      rightText,
      leftText,
      topText = '';

    if (isRtl && !this.args.ignoreRTL) {
      rightText = 'left';
      leftText = 'right';
    } else {
      rightText = 'right';
      leftText = 'left';
    }

    if (this.args.topTranslateMenu) {
      topText = `top: ${this.args.topTranslateMenu};`;
    } else if (this.args.bottomTranslateMenu) {
      topText = `bottom: ${this.args.bottomTranslateMenu};`;
    }

    if (this.args.rightOffset) {
      return htmlSafe(`${topText} ${rightText}: ${this.args.rightOffset};`);
    } else if (this.args.leftOffset) {
      return htmlSafe(`${topText} ${leftText}: ${this.args.leftOffset};`);
    }

    return htmlSafe(`${topText}`);
  }

  @action
  closeDropdown() {
    this.element.classList.remove('insert-finished');
    this.element.classList.remove('insert-transition'); // mobile animation
    this.isExpanded = false; // desktop animation

    // there is close transition effect so we need to wait for it to complete
    later(() => {
      this.args.setDropdownRef?.(null);
      document.querySelector('.c-new-navigation')?.classList.remove('dropdown--active');
      ScrollingUtils().enableWindowScroll();
    }, this.duration);
  }

  @action
  dropdownDidInsert(element) {
    this.element = element;

    next(() => {
      element.classList.add('insert-transition'); // mobile animation
      this.isExpanded = true; // desktop animation

      // below $tablet-max we show dropdowns in full screen mode
      if (window.innerWidth < 910 && !this.args.desktopVersion) {
        ScrollingUtils().disableWindowScroll();
      }

      // skip adding dropdown--active class to new navigation if dropdown is inside it,
      // this is only needed for mobile dropdowns taking whole screen size
      if (!element.closest('.c-new-navigation') && !this.args.desktopVersion) {
        document.querySelector('.c-new-navigation')?.classList.add('dropdown--active');
      }

      later(() => {
        element.classList.add('insert-finished');
      }, this.duration - 60); // show shadow a bit before animation completes, better UX
    });
  }

  @action
  clickEvent(event) {
    const itemClass = 'dropdown_list_item';
    const dontCloseClass = 'dropdown--dont-close';
    const target = event.target;

    if (target.classList.contains(dontCloseClass) || target.closest(`.${dontCloseClass}`)) {
      return;
    }

    if (target.classList.contains(itemClass) || target.closest(`.${itemClass}`)) {
      this.closeDropdown();
    }
  }
}
